import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * InlineActivityCell — compact dropdown for toggling pod members on a table row.
 * Source of truth is activity records (via ActivityService), not _userData engagement.
 *
 * Shows co-participant emojis; clicking opens a multi-select to add/remove members.
 * Initial state comes from pre-fetched _coParticipantIds (no per-row API calls).
 *
 * @param {Object} props
 * @param {Object} props.item - Collection item (must have .id and .title)
 * @param {string} props.itemType - 'movie' | 'season' | 'game' | 'boardgame' | 'book'
 * @param {string} props.activityType - 'watched' | 'played' | 'read'
 * @param {Array}  props.coParticipants - Already-resolved user objects from _coParticipantIds
 * @param {Array}  props.otherMembers - Other pod members: [{ userId, userName, displayName, emoji }]
 * @param {Object} props.currentUser - { id, name }
 * @param {string} props.pod - Pod id
 * @param {Object} props.activityService - ActivityService instance
 * @param {Function} [props.onChanged] - (newCoParticipantIds: string[]) => void
 */
export default function InlineActivityCell({
    item,
    itemType,
    activityType,
    coParticipants = [],
    otherMembers = [],
    currentUser,
    pod,
    activityService,
    onChanged,
}) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    // Local selected state: Set of userId strings
    const [selected, setSelected] = useState(() => new Set(coParticipants.map(u => u.id)));
    const [activityId, setActivityId] = useState(null);
    const [activityLoaded, setActivityLoaded] = useState(false);
    const ref = useRef(null);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // When opening for the first time, fetch the actual activity record id (needed for delete)
    const ensureActivityLoaded = useCallback(async () => {
        if (activityLoaded || !activityService || !pod || !item.id) return;
        setActivityLoaded(true);
        try {
            const result = await activityService.list({ podId: pod, limit: 200 });
            const existing = (result.items || []).find(
                a => a.itemId === item.id && a.itemType === itemType
            );
            if (existing) {
                setActivityId(existing.id);
                const others = (existing.participants || []).filter(id => id !== currentUser?.id);
                setSelected(new Set(others));
            }
        } catch (_) {}
    }, [activityLoaded, activityService, pod, item.id, itemType, currentUser?.id]);

    const handleOpen = () => {
        setOpen(o => !o);
        ensureActivityLoaded();
    };

    const persist = useCallback(async (newSelected, currentActivityId) => {
        if (!activityService || !pod || !currentUser) return currentActivityId;
        const participants = [currentUser.id, ...newSelected];
        try {
            if (newSelected.size === 0) {
                if (currentActivityId) {
                    await activityService.delete(currentActivityId);
                    return null;
                }
                return null;
            } else if (currentActivityId) {
                await activityService.delete(currentActivityId);
                const result = await activityService.create({
                    type: activityType,
                    itemType,
                    itemId: item.id,
                    itemTitle: item.title || '',
                    participants,
                    pod,
                    date: new Date().toISOString(),
                });
                return result.id;
            } else {
                const result = await activityService.create({
                    type: activityType,
                    itemType,
                    itemId: item.id,
                    itemTitle: item.title || '',
                    participants,
                    pod,
                    date: new Date().toISOString(),
                });
                return result.id;
            }
        } catch (err) {
            console.error('InlineActivityCell: failed to save activity', err);
            return currentActivityId;
        }
    }, [activityService, pod, currentUser, activityType, itemType, item.id, item.title]);

    const handleToggle = async (userId) => {
        setSaving(true);
        try {
            const next = new Set(selected);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            setSelected(next);
            const newActivityId = await persist(next, activityId);
            setActivityId(newActivityId);
            onChanged?.([...next]);
        } finally {
            setSaving(false);
        }
    };

    if (otherMembers.length === 0) return <span className="text-text-tertiary">-</span>;

    const displayMembers = otherMembers.filter(m => selected.has(m.userId));

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={handleOpen}
                className="inline-flex items-center gap-0.5 cursor-pointer hover:opacity-80 transition-opacity"
                disabled={saving}
            >
                {displayMembers.length > 0
                    ? displayMembers.map((m, i) => (
                        <span key={m.userId} className={`text-sm ${i > 0 ? '-ml-1' : ''}`}>{m.emoji}</span>
                    ))
                    : <span className="text-text-tertiary text-xs">+</span>
                }
            </button>
            {open && (
                <div className="absolute top-full left-0 mt-1 w-44 bg-surface border border-border-subtle rounded-lg shadow-lg z-50 py-1">
                    {otherMembers.map(m => {
                        const checked = selected.has(m.userId);
                        return (
                            <button
                                key={m.userId}
                                type="button"
                                disabled={saving}
                                onClick={() => handleToggle(m.userId)}
                                className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors hover:bg-surface-hover ${
                                    checked ? 'text-text-primary' : 'text-text-tertiary'
                                } ${saving ? 'opacity-50' : ''}`}
                            >
                                <span className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center text-[9px] ${
                                    checked ? 'bg-accent-primary border-accent-primary text-white' : 'border-border-subtle'
                                }`}>{checked && '✓'}</span>
                                {m.emoji} {m.displayName || m.userName}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
